import {
  Card,
  CardContent,
  Stack,
  Typography,
  Box,
  IconButton,
  Collapse,
} from "@mui/material";
import { useState, useMemo, useCallback } from "react";
import { ToolType } from "src/api/services/askApi";
import StatusChip from "../StatusChip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToolIcon from "../ToolIcon";
import FormattedResponse from "../FormattedResponse";

function toolStatus(t: ToolType, runDone: boolean): string {
  if (t.failed) return "error";
  if (t.output !== undefined) return "done";
  // No output yet:
  // - run still going  -> genuinely running
  // - run finished      -> the model answered before this tool returned
  return runDone ? "interrupted" : "running";
}

function callQueryBlock(entry: ToolType): string {
  if (!entry.args || entry.args === "{}") return "";
  try {
    const parsed = JSON.parse(entry.args);
    if (typeof parsed.query !== "string") return "";
    return "```sql\n" + parsed.query + "\n```";
  } catch {
    return "";
  }
}

function outputBlock(output: string | undefined): string {
  if (output === undefined) return "";
  const trimmed = output.trim();
  try {
    const parsed = JSON.parse(trimmed);
    return "```json\n" + JSON.stringify(parsed, null, 2) + "\n```";
  } catch {
    return "```\n" + trimmed + "\n```";
  }
}

interface Props {
  tool: ToolType;
  runDone: boolean;
}

const ToolDescription = ({ tool, runDone }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const status = useMemo(() => toolStatus(tool, runDone), [runDone, tool]);
  const queryBlock = useMemo(() => callQueryBlock(tool), [tool]);
  const hasDetail = useMemo(
    () => Boolean(queryBlock) || status !== "pending",
    [queryBlock, status],
  );

  const handleToggle = useCallback(() => setIsOpen((b) => !b), []);

  return (
    <Card key={tool.callId} variant="outlined" sx={{ borderRadius: "10px" }}>
      <CardContent sx={{ p: 0.75, "&:last-child": { pb: 0.75 } }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          justifyContent="normal"
        >
          <ToolIcon name={tool.name} />
          <Typography variant="body2" sx={{ fontWeight: 500, width: 110 }}>
            {tool.name}
          </Typography>

          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <StatusChip status={status} output={tool.output} />

            {hasDetail && (
              <IconButton
                size="small"
                onClick={handleToggle}
                aria-label={isOpen ? "Collapse" : "Expand"}
                aria-expanded={isOpen}
                sx={{
                  transition: "transform 0.2s",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Stack>

        <Collapse in={isOpen} unmountOnExit>
          <Box
            sx={{
              mt: 1,
              "& pre": {
                m: 0,
                p: 1.25,
                bgcolor: "action.hover",
                borderRadius: 1,
                overflow: "auto",
                fontSize: 12,
                lineHeight: 1.5,
              },
              "& code": { fontFamily: "monospace" },
            }}
          >
            {queryBlock && <FormattedResponse>{queryBlock}</FormattedResponse>}
            {tool.output != null && (
              <Box sx={{ mt: queryBlock ? 1 : 0 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
                >
                  Result
                </Typography>
                <FormattedResponse>
                  {outputBlock(tool.output)}
                </FormattedResponse>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ToolDescription;
